import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

export default function AnnualLeaveBalanceReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Generate year options (current year + 1 year ahead, 5 years back)
  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  const handleExport = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('[AnnualLeaveBalanceReport] Exporting for year:', selectedYear);

      const blob = await api.get(`/leave-balances/admin/annual-report/export?year=${selectedYear}`, {
        responseType: 'blob'
      });

      if (!blob || !(blob instanceof Blob)) {
        setError('No data received from server. Please try again.');
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Annual_Leave_Balances_${selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Report exported successfully for ${selectedYear}`);
    } catch (err) {
      console.error('Failed to export annual leave balance report:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to export this report.');
      } else {
        setError('Failed to export report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Annual Leave Balance Report
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export a CSV report of all employee leave balances for a given year. 
        This report is intended for payroll providers and includes entitled days, 
        carried over days, used days, and remaining balance for each employee.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {yearOptions.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? 'Exporting...' : 'Export to CSV'}
        </Button>
      </Stack>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Report Contents:</Typography>
        <Typography variant="body2" color="text.secondary">
          • Employee Name & Email<br />
          • Leave Type<br />
          • Entitled Days (annual allowance)<br />
          • Carried Over Days (from previous year)<br />
          • Used Days<br />
          • Reserved Days (pending approval)<br />
          • Remaining Days (entitled + carried - used)<br />
          • Available Now (remaining - reserved)
        </Typography>
      </Box>
    </Paper>
  );
}

